/* eslint-disable react-hooks/rules-of-hooks */
import { observer } from "mobx-react";
import {
  Button,
  Flex,
  rem,
  Text,
  Tooltip,
  Switch,
  Select,
  Slider,
} from "@mantine/core";
import { useStore } from "../../../../hooks/use-store";
import ImageObserved from "../Image";
import { PipelineParameters } from "../Pipeline";
import { useHistory } from "react-router-dom";
import { useState } from "react";

const Element = (model: {
  buy: (params: PipelineParameters[]) => Promise<void>;
  loading: boolean
}) => {

  const {
    dataStore: { spaceStore },
  } = useStore();

  const history = useHistory();
  const [mode, setMode] = useState<'standard' | 'advanced'>('advanced');
  const [sharpen, setSharpen] = useState<number>(7);
  const [smartGrain, setSmartGrain] = useState<number>(7);
  const [ultraDetail, setUltraDetail] = useState<number>(30);
  const [flavor, setFlavor] = useState<string>('photo_denoiser');
  const [scaleFactor, setScaleFactor] = useState<string>('2x');
  const scaleToCredits: Record<string, number> = { '2x': 4, '4x': 15, '8x': 30, '16x': 80 };
  const selectedCredits = scaleToCredits[scaleFactor];

  return (
    <Flex w={'100%'} mt={12} pt={32} pb={32} pl={40} pr={40} style={{ borderRadius: '4px', background: 'rgba(32, 32, 32, 1)' }} gap={32} pos='relative'>
      <Flex pos='absolute' top='0' right='0' style={{ cursor: 'pointer' }} onClick={() => {
        history.push('/history')
      }}>
        <svg width="40" height="40" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.17157 11.2944L10.8284 5.63758" stroke="#BDBDBD" stroke-linecap="round" stroke-linejoin="round"></path><path d="M10.8284 11.2945L5.17157 5.6377" stroke="#BDBDBD" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>
      </Flex>
      <Flex direction={'column'} w={'40%'} gap={12}>

        <Text fz={32} fw={800} color='#fff' lh={1.2} mb={12}>Upscale Images
        </Text>
        <Text fz={16} color='#fff' lh={1.4} maw={440}>
          Generate higher-resolution versions of your visuals. <Tooltip
            label={
              <Text fw={500}>
                Generated images can be enlarged to higher resolutions.
                The process increases image size while maintaining overall visual quality, giving you more flexibility for post-production, layout variations, or large-format output.

              </Text>
            }
            multiline
            w={320}
            withArrow
            position="bottom"
            color='dark'
          >
            <span style={{ marginLeft: 4, cursor: 'pointer', display: 'inline-block' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="#4badf4" class="icon icon-tabler icons-tabler-filled icon-tabler-info-square-rounded"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M12 2l.642 .005l.616 .017l.299 .013l.579 .034l.553 .046c4.687 .455 6.65 2.333 7.166 6.906l.03 .29l.046 .553l.041 .727l.006 .15l.017 .617l.005 .642l-.005 .642l-.017 .616l-.013 .299l-.034 .579l-.046 .553c-.455 4.687 -2.333 6.65 -6.906 7.166l-.29 .03l-.553 .046l-.727 .041l-.15 .006l-.617 .017l-.642 .005l-.642 -.005l-.616 -.017l-.299 -.013l-.579 -.034l-.553 -.046c-4.687 -.455 -6.65 -2.333 -7.166 -6.906l-.03 -.29l-.046 -.553l-.041 -.727l-.006 -.15l-.017 -.617l-.004 -.318v-.648l.004 -.318l.017 -.616l.013 -.299l.034 -.579l.046 -.553c.455 -4.687 2.333 -6.65 6.906 -7.166l.29 -.03l.553 -.046l.727 -.041l.15 -.006l.617 -.017c.21 -.003 .424 -.005 .642 -.005zm0 9h-1l-.117 .007a1 1 0 0 0 0 1.986l.117 .007v3l.007 .117a1 1 0 0 0 .876 .876l.117 .007h1l.117 -.007a1 1 0 0 0 .876 -.876l.007 -.117l-.007 -.117a1 1 0 0 0 -.764 -.857l-.112 -.02l-.117 -.006v-3l-.007 -.117a1 1 0 0 0 -.876 -.876l-.117 -.007zm.01 -3l-.127 .007a1 1 0 0 0 0 1.986l.117 .007l.127 -.007a1 1 0 0 0 0 -1.986l-.117 -.007z" /></svg>
            </span>
          </Tooltip>

        </Text>
        <Flex direction={'column'} gap={8} mt={8}>
          <Switch
            size="xl"
            color="orange"
            onLabel="Precision V2"
            offLabel="Creative V2"
            checked={mode === 'advanced'}
            onChange={(event) => setMode(event.currentTarget.checked ? 'advanced' : 'standard')}
            styles={{
              label: { color: 'white', fontWeight: 600 },
            }}
          />
        </Flex>
        {mode === 'advanced' && (
          <Flex direction={'column'} gap={12} mt={12}>
            <Flex direction={'column'} gap={4}>
              <Text fz={12} color="#bdbdbd">Sharpen</Text>
              <Text fz={11} color="#8f8f8f">
                Image sharpness intensity control. Higher values increase edge definition and clarity in the upscaled image.
              </Text>
              <Slider
                value={sharpen}
                onChange={setSharpen}
                min={0}
                max={100}
                step={1}
                color="orange"
                styles={{
                  markLabel: { color: 'white' },
                }}
                label={(value) => `${value}`}
              />
            </Flex>
            <Flex direction={'column'} gap={4}>
              <Text fz={12} color="#bdbdbd">Smart grain</Text>
              <Text fz={11} color="#8f8f8f">
                Intelligent grain/texture enhancement for the upscaled image. Helps preserve natural film grain and texture detail.
                Higher values add more fine-grained texture to prevent the upscaled image from looking too smooth or artificial.

              </Text>
              <Slider
                value={smartGrain}
                onChange={setSmartGrain}
                min={0}
                max={100}
                step={1}
                color="orange"
                styles={{
                  markLabel: { color: 'white' },
                }}
                label={(value) => `${value}`}
              />
            </Flex>
            <Flex direction={'column'} gap={4}>
              <Text fz={12} color="#bdbdbd">Ultra detail</Text>
              <Text fz={11} color="#8f8f8f">
                Ultra detail enhancement level for the image upscaling process. This parameter controls how much fine detail
                the AI adds to the upscaled image, such as textures, patterns, and micro-details.
                Higher values create more intricate details.
              </Text>
              <Slider
                value={ultraDetail}
                onChange={setUltraDetail}
                min={0}
                max={100}
                step={1}
                color="orange"
                styles={{
                  markLabel: { color: 'white' },
                }}
                label={(value) => `${value}`}
              />
            </Flex>
            <Flex direction={'column'} gap={4}>
              <Text fz={12} color="#bdbdbd">Flavor</Text>
              <Text fz={11} color="#8f8f8f">
                Image processing flavor that determines the upscaling style and optimization for different image types:

                <Text fz={11} color="#8f8f8f">- sublime: Optimized for artistic and illustrated images with smooth gradients and vibrant colors</Text>
                <Text fz={11} color="#8f8f8f">- photo: Optimized for photographic images, preserving natural colors and realistic details</Text>
                <Text fz={11} color="#8f8f8f">- photo_denoiser: Specialized for photos with noise reduction, ideal for low-light or grainy photographs</Text>
              </Text>
              <Select

                value={flavor}
                onChange={(v) => setFlavor(v || '')}
                placeholder="Select flavor"
                data={[
                  { value: 'sublime', label: 'sublime' },
                  { value: 'photo', label: 'photo' },
                  { value: 'photo_denoiser', label: 'photo_denoiser' },
                ]}
                styles={{
                  input: {
                    color: 'white',
                    backgroundColor: 'transparent',
                    borderColor: '#464646',
                  },
                  dropdown: {
                    backgroundColor: 'rgba(20,20,20,1)',
                    borderColor: '#464646',
                  },
                  item: {
                    color: 'white',
                  },
                }}
                clearable
              />
            </Flex>
          </Flex>
        )}
        <Flex gap={12} mt={32} align={'center'}>
          <Text fz={12} color="#bdbdbd">Scale factor</Text>
          <Select
            w={'80px'}
            value={scaleFactor}
            onChange={(v) => setScaleFactor(v || '2x')}
            placeholder="Select scale"
            data={[
              { value: '2x', label: '2x' },
              { value: '4x', label: '4x' },
              { value: '8x', label: '8x' },
              { value: '16x', label: '16x' },
            ]}
            styles={{
              input: {
                color: 'white',
                backgroundColor: 'transparent',
                borderColor: '#464646',
              },
              dropdown: {
                backgroundColor: 'rgba(20,20,20,1)',
                borderColor: '#464646',
              },
              item: {
                color: 'white',
              },
            }}
          />
          <Tooltip
            p={12}
            label={
              <span>Generation cost:
                &nbsp;
                <span style={{
                  color: '#f53c02',
                  fontWeight: 700
                }}>{selectedCredits} credits
                </span>
              </span>
            }
            withArrow
            position="right"
            color='dark'
          >
            <Button
              styles={{
                root: {
                  color: 'rgba(230, 230, 230, 1)',
                  height: rem(36),
                  fontSize: '14px',
                  background: '#f53c02 !important',
                  border: 'none',
                  position: 'relative',
                },
              }}
              variant="default"
              onClick={async () => {
                const pipeline: PipelineParameters = {
                  provider: 'custom',
                  type: 'upscaleV2',
                  inputSrc: spaceStore.selectedImage!,
                  scaleFactor: scaleFactor as any,
                  srcset: [],
                  credits: selectedCredits,
                  model: mode === 'advanced' ? 'v2' : 'v1',
                }
                if (mode === 'advanced') {
                  (pipeline).sharpen = sharpen;
                  (pipeline).smart_grain = smartGrain;
                  (pipeline).ultra_detail = ultraDetail;
                  if (flavor.trim().length > 0) {
                    (pipeline).flavor = flavor.trim();
                  }
                }
                await model.buy([pipeline]);
              }}
            >
              Upscale image
            </Button>
          </Tooltip>
        </Flex>

      </Flex>
      <Flex justify={'center'} direction={'column'} align={'center'} w={'60%'}>
        <ImageObserved
          width={'100%'}
          height={'100%'}
          dark
          src={spaceStore.selectedImage!}
        />
      </Flex>
    </Flex>
  );
};

export const UpscaleMaginific = observer(Element);